// Placeholder service layer for Trust & Safety features.
// Swap the in-memory data for Supabase queries when the verification tables ship.

export type VerificationLevel = "unverified" | "basic" | "verified" | "premium";

export interface VerifiedRecruiter {
  id: string;
  name: string;
  agency: string;
  country: string;
  specialties: string[];
  level: VerificationLevel;
  since: string;
  placements: number;
  rating: number;
}

export interface VisaScamWarning {
  id: string;
  country: string;
  title: string;
  summary: string;
  reportedCount: number;
  severity: "low" | "medium" | "high" | "critical";
  updatedAt: string;
  redFlags: string[];
}

export interface SafetyAlert {
  id: string;
  region: string;
  title: string;
  body: string;
  category: "recruitment" | "visa" | "travel" | "housing" | "financial";
  severity: "info" | "warning" | "critical";
  issuedAt: string;
}

export interface VerifiedSource {
  id: string;
  name: string;
  category: "government" | "embassy" | "ngo" | "media" | "university";
  country: string;
  url: string;
  description: string;
  verifiedAt: string;
}

export interface OpportunityReport {
  id: string;
  opportunityUrl: string;
  opportunityType: "job" | "scholarship" | "visa" | "recruiter" | "other";
  reason: string;
  details: string;
  submittedAt: string;
  status: "pending" | "reviewing" | "resolved" | "dismissed";
}

export const VERIFIED_RECRUITERS: VerifiedRecruiter[] = [
  {
    id: "r1",
    name: "Amelia Okafor",
    agency: "Global Talent Partners",
    country: "United Kingdom",
    specialties: ["Healthcare", "Tech"],
    level: "premium",
    since: "2022-03-14",
    placements: 184,
    rating: 4.9,
  },
  {
    id: "r2",
    name: "Rajesh Menon",
    agency: "Bridge Careers",
    country: "United Arab Emirates",
    specialties: ["Engineering", "Logistics"],
    level: "verified",
    since: "2023-06-01",
    placements: 92,
    rating: 4.7,
  },
  {
    id: "r3",
    name: "Sofia Lindqvist",
    agency: "Nordic Move",
    country: "Sweden",
    specialties: ["Skilled Trades"],
    level: "verified",
    since: "2024-01-10",
    placements: 41,
    rating: 4.6,
  },
  {
    id: "r4",
    name: "Daniel Otieno",
    agency: "East Africa Careers",
    country: "Kenya",
    specialties: ["Hospitality", "Agriculture"],
    level: "basic",
    since: "2024-08-22",
    placements: 12,
    rating: 4.2,
  },
];

export const VISA_SCAM_WARNINGS: VisaScamWarning[] = [
  {
    id: "v1",
    country: "Canada",
    title: "Fake LMIA job offers via WhatsApp",
    summary:
      "Scammers charge USD 2,000–5,000 for fabricated Labour Market Impact Assessment letters.",
    reportedCount: 312,
    severity: "critical",
    updatedAt: "2026-06-18",
    redFlags: ["Upfront LMIA fee", "Contact only via WhatsApp", "No employer website"],
  },
  {
    id: "v2",
    country: "Germany",
    title: "Bogus Chancenkarte agents",
    summary: "Unregistered agents promise fast-track Opportunity Card approvals for a fee.",
    reportedCount: 148,
    severity: "high",
    updatedAt: "2026-06-02",
    redFlags: ["Guaranteed approval", "No BAMF registration", "Crypto payments"],
  },
  {
    id: "v3",
    country: "Australia",
    title: "Fake 482 sponsorship listings",
    summary: "Listings on classifieds sites request passport scans before an interview.",
    reportedCount: 87,
    severity: "high",
    updatedAt: "2026-05-27",
    redFlags: ["Passport scan requested early", "Generic Gmail contact", "No ABN listed"],
  },
  {
    id: "v4",
    country: "United Arab Emirates",
    title: "Visit-visa-to-work bait",
    summary: "Recruiters lure workers on visit visas then withhold documents on arrival.",
    reportedCount: 61,
    severity: "critical",
    updatedAt: "2026-04-11",
    redFlags: ["Enter on visit visa", "Passport held on arrival", "Salary paid in cash"],
  },
];

export const SAFETY_ALERTS: SafetyAlert[] = [
  {
    id: "a1",
    region: "Gulf Region",
    title: "Kafala contract-swap scheme resurging",
    body: "Workers report signing English contracts and receiving Arabic contracts with lower salaries on arrival.",
    category: "recruitment",
    severity: "critical",
    issuedAt: "2026-06-24",
  },
  {
    id: "a2",
    region: "Europe",
    title: "Fake Blue Card portals",
    body: "Cloned websites mimic official EU portals and collect application fees.",
    category: "visa",
    severity: "warning",
    issuedAt: "2026-06-20",
  },
  {
    id: "a3",
    region: "Southeast Asia",
    title: "Cyber-scam compound recruitment",
    body: "Fake tech jobs in border regions have been linked to trafficking. Verify employer address in person.",
    category: "recruitment",
    severity: "critical",
    issuedAt: "2026-06-15",
  },
  {
    id: "a4",
    region: "Global",
    title: "Cryptocurrency-only salary offers",
    body: "Employers requesting salary paid only in crypto with no written contract are a strong scam signal.",
    category: "financial",
    severity: "warning",
    issuedAt: "2026-06-10",
  },
];

export const VERIFIED_SOURCES: VerifiedSource[] = [
  {
    id: "s1",
    name: "IRCC — Government of Canada",
    category: "government",
    country: "Canada",
    url: "https://www.canada.ca/en/immigration-refugees-citizenship.html",
    description: "Official Canadian immigration authority — programs, fees, processing times.",
    verifiedAt: "2026-06-01",
  },
  {
    id: "s2",
    name: "GOV.UK Visas & Immigration",
    category: "government",
    country: "United Kingdom",
    url: "https://www.gov.uk/browse/visas-immigration",
    description: "UK Home Office official portal for work, study and family visas.",
    verifiedAt: "2026-06-01",
  },
  {
    id: "s3",
    name: "Make it in Germany",
    category: "government",
    country: "Germany",
    url: "https://www.make-it-in-germany.com/en/",
    description: "Federal government portal for skilled workers immigrating to Germany.",
    verifiedAt: "2026-06-01",
  },
  {
    id: "s4",
    name: "IOM — International Organization for Migration",
    category: "ngo",
    country: "Global",
    url: "https://www.iom.int/",
    description: "UN migration agency, safe-migration resources and country offices.",
    verifiedAt: "2026-06-01",
  },
  {
    id: "s5",
    name: "DAAD Scholarship Database",
    category: "university",
    country: "Germany",
    url: "https://www2.daad.de/deutschland/stipendium/datenbank/en/",
    description: "Official DAAD listing of scholarships for international students.",
    verifiedAt: "2026-06-01",
  },
];

const REPORTS_KEY = "goh.trust.reports";

function readReports(): OpportunityReport[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(REPORTS_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function writeReports(reports: OpportunityReport[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
}

export function listOpportunityReports(): OpportunityReport[] {
  return readReports().sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
}

export function submitOpportunityReport(
  input: Omit<OpportunityReport, "id" | "submittedAt" | "status">,
): OpportunityReport {
  const report: OpportunityReport = {
    ...input,
    id: crypto.randomUUID(),
    submittedAt: new Date().toISOString(),
    status: "pending",
  };
  writeReports([report, ...readReports()]);
  return report;
}

// Heuristic fake-job detector. Returns risk 0–100 plus matched signals.
export interface FakeJobSignal {
  label: string;
  weight: number;
  matched: boolean;
}
export interface FakeJobResult {
  score: number;
  verdict: "low" | "medium" | "high";
  signals: FakeJobSignal[];
}

const FAKE_JOB_RULES: Array<{ rx: RegExp; label: string; weight: number }> = [
  {
    rx: /\b(processing|registration|visa)\s*fee\b/i,
    label: "Requests a processing / registration fee",
    weight: 30,
  },
  {
    rx: /\bwhats\s*app|telegram|wechat\b/i,
    label: "Contact via personal messaging only",
    weight: 15,
  },
  { rx: /\bguarantee(d)?\b|100%/i, label: "Guaranteed placement promise", weight: 15 },
  { rx: /\burgent(ly)?\b|immediately|today only/i, label: "High-pressure urgency", weight: 10 },
  {
    rx: /\bpassport (copy|scan|photo)\b/i,
    label: "Requests passport scan before interview",
    weight: 20,
  },
  {
    rx: /\bcrypto|bitcoin|western union|money ?gram\b/i,
    label: "Unusual payment method",
    weight: 25,
  },
  {
    rx: /@(gmail|yahoo|outlook|hotmail)\./i,
    label: "Only a free email address (no company domain)",
    weight: 10,
  },
  {
    rx: /\bno (experience|qualifications) (required|needed)\b/i,
    label: "No experience required for a high-salary role",
    weight: 10,
  },
  {
    rx: /\b(usd|eur|gbp)\s?\d{4,}/i,
    label: "Unusually high salary quoted without context",
    weight: 10,
  },
];

export function detectFakeJob(text: string): FakeJobResult {
  const signals: FakeJobSignal[] = FAKE_JOB_RULES.map((r) => ({
    label: r.label,
    weight: r.weight,
    matched: r.rx.test(text),
  }));
  const score = Math.min(
    100,
    signals.filter((s) => s.matched).reduce((a, s) => a + s.weight, 0),
  );
  const verdict: FakeJobResult["verdict"] = score >= 50 ? "high" : score >= 25 ? "medium" : "low";
  return { score, verdict, signals };
}

export const VERIFICATION_BADGE_LEVELS: Record<
  VerificationLevel,
  { label: string; description: string }
> = {
  unverified: { label: "Unverified", description: "No verification checks completed yet." },
  basic: {
    label: "Basic Verified",
    description: "Business registration and contact details confirmed.",
  },
  verified: {
    label: "Verified",
    description: "Registration, licence and reference checks completed.",
  },
  premium: {
    label: "Premium Verified",
    description: "Full audit including on-site check, licence, and community reviews.",
  },
};
